// ============================================================================
// lazyRetry.js — Import lazy résilient aux déploiements.
// ============================================================================
// Chaque déploiement renouvelle les hashs des chunks JS. Une app déjà OUVERTE
// qui navigue ensuite vers un module pas encore visité demande alors un chunk
// qui n'existe plus (404) → l'import dynamique rejette → écran blanc sous le
// header (vécu en prod : page « Simulateur de paie » vide juste après un
// déploiement). Ce wrapper recharge automatiquement la page UNE fois pour
// récupérer l'index.html et les chunks à jour — garde anti-boucle de 30 s
// (si le rechargement ne règle pas le problème, l'erreur réelle est relancée
// et remonte au logger erreursFront).
// ============================================================================
import { lazy, useState, createElement } from "react";

const KEY = "aramis_chunk_reload_at";

/**
 * Le même filet, pour un import dynamique NU (dans un gestionnaire de clic,
 * pas dans un composant lazy) : générateurs .docx, SharePoint, jsPDF…
 * Après un déploiement, l'onglet resté ouvert demande un chunk qui n'existe
 * plus et l'action échoue sans rien dire (« Génération de la convocation
 * échouée » — signalé par Pierre le 08/09/2026). On recharge une fois pour
 * récupérer l'index et les chunks à jour ; au-delà, l'erreur réelle remonte.
 */
export async function importRetry(importFn) {
  try {
    return await importFn();
  } catch (err) {
    const chunkManquant = /Loading chunk|Failed to fetch dynamically imported|error loading dynamically imported|Importing a module script failed/i
      .test(String((err && err.message) || err));
    if (chunkManquant) {
      try {
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last > 30000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return await new Promise(() => {});   // rechargement en cours
        }
      } catch { /* sessionStorage indisponible */ }
      const e = new Error("Une nouvelle version d'Aramis vient d'être déployée. Rechargez la page (Cmd+R) puis réessayez.");
      e.cause = err;
      throw e;
    }
    throw err;
  }
}

export function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      try {
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last > 30000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return new Promise(() => {}); // rechargement en cours — suspend le rendu
        }
      } catch { /* sessionStorage indisponible (mode privé strict) */ }
      throw err;
    })
  );
}

/**
 * Comme lazyRetry, avec deux capacités de plus pour la coquille (lot L6,
 * préchargement des vues) :
 *   • Composant.preload() lance le téléchargement du morceau SANS rendre la
 *     vue (intention de l'utilisateur, attente avant bascule dans navTo) ;
 *   • une fois le module arrivé, le composant se rend D'UN COUP, sans passer par
 *     le lazy de React : un React.lazy jamais rendu « suspend » une fois même si
 *     son module est déjà là (la promesse se résout au tour suivant), et une
 *     transition de vue capturerait alors l'écran de chargement.
 * Le choix (direct ou paresseux) est figé au MONTAGE de chaque instance
 * (useState) : une instance montée par le lazy n'en change plus, ce qui
 * démonterait la vue et perdrait son état.
 *
 * PRÉCHARGER NE RECHARGE JAMAIS LA PAGE. preload() part d'un simple survol,
 * d'un appui ou d'un focus clavier sur une entrée de menu : après un
 * déploiement, le morceau demandé n'existe plus (404) et le filet de
 * rechargement, s'il jouait ici, effaçait une saisie en cours quand la souris
 * traversait le rail (relecture L6). preload() résout donc à false, sans
 * autre effet. Seul le RENDU de la vue, donc l'intention explicite de quitter
 * la page, garde le filet de lazyRetry.
 */
export function lazyPrechargeable(importFn) {
  let module = null;
  let promesse = null;
  const importer = () => {
    if (!promesse) {
      promesse = importFn().then((m) => { module = m; return m; }, (err) => { promesse = null; throw err; });
    }
    return promesse;
  };
  const Paresseux = lazy(() => importer().catch((err) => {
    try {
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 30000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {}); // rechargement en cours
      }
    } catch { /* sessionStorage indisponible (mode privé strict) */ }
    throw err;
  }));
  function Composant(props) {
    const [direct] = useState(() => module);
    return direct ? createElement(direct.default, props) : createElement(Paresseux, props);
  }
  Composant.preload = () => importer().then(() => true, () => false);
  Composant.estCharge = () => module !== null;
  return Composant;
}
